import { KeyboardEvent, useState } from 'react';
import cn from 'classnames';
import styles from './simple-rating.module.scss';

const RATINGS = Array.from({ length: 10 }, (_, i) => i + 1);
const WORDS = [
  'Not for me',
  'Disappointing',
  'Weak',
  'Meh',
  'Okay',
  'Decent',
  'Good',
  'Really good',
  'Excellent',
  'A new favourite',
];

type SimpleRatingProps = {
  value: number | null;
  onChange: (value: number) => void;
  invalid?: boolean;
};

export const SimpleRating = ({
  value,
  onChange,
  invalid,
}: SimpleRatingProps) => {
  const [hover, setHover] = useState<number | null>(null);
  const active = hover ?? value ?? 0;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step =
      { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[event.key] ??
      0;

    if (step) {
      event.preventDefault();
      const next = Math.max(1, Math.min(10, (value ?? 0) + step));
      onChange(next);
      event.currentTarget
        .querySelector<HTMLButtonElement>(`[data-rating="${next}"]`)
        ?.focus();
    }
  };

  return (
    <div className={styles.rating}>
      <div
        className={cn(styles.stars, { [styles.invalid]: invalid })}
        role="radiogroup"
        aria-label="Your rating"
        onMouseLeave={() => setHover(null)}
        onKeyDown={handleKeyDown}
      >
        {RATINGS.map((rating) => (
          <button
            key={rating}
            type="button"
            role="radio"
            aria-checked={value === rating}
            aria-label={`${rating} out of 10`}
            data-rating={rating}
            tabIndex={
              value === rating || (value === null && rating === 1) ? 0 : -1
            }
            className={cn(styles.star, { [styles.on]: rating <= active })}
            onMouseEnter={() => setHover(rating)}
            onClick={() => onChange(rating)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z" />
            </svg>
          </button>
        ))}
      </div>
      <span className={styles.caption} aria-live="polite">
        {active > 0 && (
          <>
            <b>{active}/10</b> {WORDS[active - 1]}
          </>
        )}
      </span>
    </div>
  );
};
